import type { Moment } from "moment"
import { BaseModel } from "@/models/base.model"
import { ModelFactory } from "@/factories/model-factory"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model"
import { WorkspaceAudienceGroupMember } from "@/workspace/models/workspace-audience-group-member.model"
import type { IWorkspaceAudienceGroup } from "@/workspace/interfaces/api/workspace-audience-group.interface"

export class WorkspaceAudienceGroup extends BaseModel implements IWorkspaceAudienceGroup {
  public static className = "WorkspaceAudienceGroup" // Used for socket communication.
  public static verboseName = "werkruimtegroep"

  public static override managedResolveFields = ["members"]

  declare id: string
  name: string
  members: WorkspaceAudienceGroupMember[]
  /** Set by the server when the group is archived. */
  archivedAt: Moment | null

  constructor(group: IWorkspaceAudienceGroup, managed = false) {
    super(group)

    this.name = group.name
    this.members = ModelFactory.childModels(WorkspaceAudienceGroupMember, group.members, managed)
    this.archivedAt = group.archivedAt ?? null
  }

  /** The members as the pickers read them. */
  public get memberIds(): string[] {
    return this.memberContacts.map((contact) => contact.id)
  }

  public get memberContacts(): KnownBusinessEntityContact[] {
    return this.members.map((member) => member.knownBusinessEntityContact)
  }

  /** Apply picker selections while retaining the identity of unchanged memberships. */
  public setMembers(contacts: KnownBusinessEntityContact[]): void {
    const existing = new Map(
      this.members.map((member) => [member.knownBusinessEntityContact.id, member])
    )
    this.members = contacts.map(
      (contact) =>
        existing.get(contact.id) ??
        ModelFactory.createModel(
          WorkspaceAudienceGroupMember,
          { audienceGroup: this.id, knownBusinessEntityContact: contact },
          this.isManaged
        )
    )
  }
}
